import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { short } from "@/lib/creator360";
import { comma, day, mins, num1 } from "@/lib/c360Profile";
import { VideoCard } from "@/components/c360/profile/YouTubeExtras";
import { bl, useBlur } from "@/lib/c360Mine";

export function LiveTag({ tag }) {
  if (!tag) return null;
  const now = tag === "live now";
  return <span className="mono text-[9px] tracking-wider px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap" style={now ? { color: "#F43F5E", background: "rgba(244,63,94,0.12)" } : { color: "#A1A1AA", background: "rgba(255,255,255,0.06)" }} data-testid="live-row-tag">{tag}</span>;
}

export function TopLives({ d }) {
  const top = [...d.live.lives30].sort((a, b) => b.views - a.views).slice(0, 3);
  if (!top.length) return null;
  return (
    <section data-testid="c360-top-lives">
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Top 3 lives by views · last 30 days</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {top.map((v, i) => (
          <VideoCard key={v.vid} v={v} i={i} testid={`c360-top-live-${i}`} extra={
            <div className="mono text-[10px] text-zinc-400 mt-1">{mins(v.mins)} long · {v.avg != null ? `${comma(v.avg)} watching live${v.peak != null ? ` · peak ${comma(v.peak)}` : ""}` : "Live crowd not measured on this one"}</div>
          } />
        ))}
      </div>
    </section>
  );
}

const th = "px-3 py-2.5 text-left font-normal mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 whitespace-nowrap";

export function LivesTable({ d }) {
  const blur = useBlur();
  const [open, setOpen] = useState(false);
  const rows = d.live.lives30;
  if (!rows.length) return null;
  return (
    <section className="border hairline rounded-lg overflow-hidden" style={{ background: "var(--surface)" }} data-testid="c360-lives-table">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="c360-lives-toggle">
        <span className="text-sm text-zinc-200">View their lives in the last 30 days ({rows.length})</span>
        <ChevronDown size={15} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t hairline">
          <p className="px-5 py-3 text-xs text-zinc-500" data-testid="c360-lives-note">Every live on record for them in the last 30 days, from the video logs and Live Watch, matched by video so none is counted twice. Live viewer numbers show only for lives Live Watch measured while they were on. Promoted shows the exchange the live named.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[980px]">
              <thead><tr className="border-t hairline">{["Date", "Live stream", "Promoted", "Length", "Avg live", "Peak", "Views", "Likes", "Chat/min"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>{rows.map((l) => (
                <tr key={l.vid} className="border-t hairline" data-testid="c360-live-row" data-vid={l.vid}>
                  <td className="px-3 py-2 mono text-xs text-zinc-400 whitespace-nowrap">{day(l.date)}</td>
                  <td className="px-3 py-2 max-w-[340px]"><a href={l.url} target="_blank" rel="noreferrer" className={`flex items-start gap-2 text-zinc-200 hover:text-cyan-300 ${bl(blur, "link")}`}><span className="line-clamp-1">{l.title}</span><LiveTag tag={l.tag} /></a></td>
                  <td className="px-3 py-2 mono text-[11px] text-zinc-400">{l.ex?.length ? l.ex.join(", ") : "—"}</td>
                  <td className="px-3 py-2 mono text-xs">{mins(l.mins)}</td>
                  <td className="px-3 py-2 mono text-xs" data-testid="c360-live-avg">{l.avg != null ? comma(l.avg) : "—"}</td>
                  <td className="px-3 py-2 mono text-xs">{l.peak != null ? comma(l.peak) : "—"}</td>
                  <td className="px-3 py-2 mono text-xs">{short(l.views)}</td>
                  <td className="px-3 py-2 mono text-xs">{l.likes > 0 ? comma(l.likes) : "—"}</td>
                  <td className="px-3 py-2 mono text-xs">{l.chat != null ? num1(l.chat) : "—"}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export function IgReels({ d }) {
  const blur = useBlur();
  const [open, setOpen] = useState(false);
  const reels = d.nums.ig.reels || [];
  if (!reels.length) return <p className="text-sm text-zinc-400" data-testid="c360-reels-none">Their top reels are not saved yet. They appear after Creator 360's next Instagram check.</p>;
  return (
    <section className="border hairline rounded-lg overflow-hidden" style={{ background: "var(--surface)" }} data-testid="c360-reels">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="c360-reels-toggle">
        <span className="text-sm text-zinc-200">View their top {reels.length} reels by views (Instagram embed)</span>
        <ChevronDown size={15} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {/* Embeds load while closed, so opening is instant. */}
      <div className={open ? "border-t hairline p-4 grid grid-cols-1 md:grid-cols-3 gap-4" : "h-0 overflow-hidden"} data-testid="c360-reels-body" aria-hidden={!open}>
        {reels.map((u, i) => (
          <div key={u} data-testid={`c360-reel-${i}`} className="overflow-hidden">
            <div className="mono text-[11px] text-cyan-400 mb-1.5">#{i + 1}</div>
            <iframe title={`Top reel ${i + 1}`} src={`${u}embed`} className={`w-full rounded-md bg-white ${bl(blur, "media")}`} style={{ height: 560, border: 0 }} loading="eager" allowFullScreen />
          </div>
        ))}
      </div>
    </section>
  );
}
